<template>
  <div class="year-view-container">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <span class="toolbar-title">年度日程视图</span>
        <span class="year-picker">
          <i class="el-icon-minus" @click="changeYear(-1)"></i>
          <span class="year-value">{{ currentYear }}</span>
          <i class="el-icon-plus" @click="changeYear(1)"></i>
        </span>
      </div>
      <div class="toolbar-right">
        <span class="toolbar-info">全年 {{ totalDays }} 天 · {{ totalCount }} 位讲师</span>
        <div class="toolbar-legend" v-if="scheduleTypeList.length">
          <span v-for="type in scheduleTypeList" :key="type.atstId" class="legend-item">
            <span class="legend-color-block" :style="{ backgroundColor: type.atstColor }"></span>
            <span class="legend-text">{{ type.atstName }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- 表格区域 -->
    <div class="table-area">
      <!-- 表格容器 - 使用原生table实现 -->
      <div class="table-wrapper" ref="tableWrapper">
        <!-- 表头区域 -->
        <div class="table-header-container" ref="headerContainer">
          <table class="year-table" :style="{ width: totalTableWidth + 'px' }">
            <!-- 列宽控制 -->
            <colgroup>
              <col style="width: 60px; min-width: 60px;">
              <col v-for="day in dayHeaders" :key="'col-' + day.sacId"
                :style="{ width: getCellWidth(day.sacType) + 'px', minWidth: getCellWidth(day.sacType) + 'px' }">
            </colgroup>
            <!-- 表头（合并三行） -->
            <thead>
              <!-- 第一行：月份 -->
              <tr class="header-row month-row">
                <th class="fixed-col lecturer-col" rowspan="3">讲师</th>
                <th v-for="(month, monthIndex) in monthHeaders" :key="'month-' + monthIndex" :colspan="month.days"
                  class="month-cell header-clickable" @click="handleMonthHeaderClick(monthIndex)">
                  {{ month.label }}
                </th>
              </tr>
              <!-- 第二行：日期和特殊类型 -->
              <tr class="header-row day-week-row">
                <th v-for="(day) in dayHeaders" :key="'day-' + day.sacId" class="day-cell"
                  :class="{ 'is-special': day.isSpecial, 'is-today': isToday(day.year, day.month, day.day) }"
                  :rowspan="day.isSpecial ? 2 : 1">
                  {{ day.isSpecial ? day.weekDes : day.day }}
                </th>
              </tr>
              <!-- 第三行：星期（仅普通日期显示，特殊类型由第二行rowspan覆盖） -->
              <tr class="header-row week-row">
                <th v-for="day in normalDayHeaders" :key="'week-' + day.sacId" class="week-cell header-clickable"
                  :class="{ 'is-today': isToday(day.year, day.month, day.day) }"
                  @click="handleWeekHeaderClick(day)">
                  {{ day.weekDes }}
                </th>
              </tr>
            </thead>
          </table>
        </div>

        <!-- 表格内容区域 - 支持横向和纵向滚动懒加载 -->
        <div class="table-body-container" ref="bodyContainer" @scroll="handleScroll">
          <table class="year-table" :style="{ width: totalTableWidth + 'px' }">
            <!-- 列宽控制 -->
            <colgroup>
              <col style="width: 60px; min-width: 60px;">
              <col v-for="day in dayHeaders" :key="'col-' + day.sacId"
                :style="{ width: getCellWidth(day.sacType) + 'px', minWidth: getCellWidth(day.sacType) + 'px' }">
            </colgroup>
            <tbody>
              <tr v-for="rowData in visibleRowCells" :key="rowData.lecturer.teacherId" class="lecturer-row">
                <!-- 固定讲师列 -->
                <td class="fixed-col lecturer-cell">
                  <div class="lecturer-name">{{ rowData.lecturer.teacherName }}</div>
                </td>
                <!-- 日期单元格（处理合并） -->
                <template v-for="cell in rowData.cells">
                  <td v-if="cell.isSpecial && cell.isSpecialDay" :key="'special-day-' + rowData.lecturer.teacherId + '-' + cell.sacId"
                    class="schedule-cell is-special"
                    :colspan="cell.colspan"
                    :data-sac-id="cell.sacId"
                    :data-teacher-id="rowData.lecturer.teacherId"
                    :style="{ backgroundColor: cell.atstColor || '#fafafa' }"
                    @click="handleCellClickBySacId(cell.sacId, rowData.lecturer, $event)">
                    <div class="cell-content">
                      <span class="schedule-text">{{ cell.displayText || cell.trainTimeLength }}</span>
                    </div>
                  </td>
                  <td v-else-if="cell.isSpecial" :key="'special-empty-' + rowData.lecturer.teacherId + '-' + cell.sacId"
                    class="schedule-cell is-special"
                    :colspan="cell.colspan">
                  </td>
                  <td v-else-if="cell.isMerged" :key="'merged-' + rowData.lecturer.teacherId + '-' + cell.sacId"
                    class="schedule-cell"
                    :colspan="cell.colspan"
                    :data-sac-id="cell.sacId"
                    :data-teacher-id="rowData.lecturer.teacherId"
                    :data-date="cell.dateStr"
                    :style="{ backgroundColor: cell.mergeInfo.schedule.atstColor }"
                    @click="handleCellClickBySacId(cell.sacId, rowData.lecturer, $event)">
                    <div class="cell-content">
                      <span class="schedule-text"
                        :title="cell.mergeInfo.schedule.trainCustomerName || ''">
                        {{ cell.mergeInfo.schedule.trainCustomerName || '' }}
                      </span>
                    </div>
                  </td>
                  <td v-else :key="'normal-' + rowData.lecturer.teacherId + '-' + cell.sacId"
                    class="schedule-cell"
                    :colspan="cell.colspan"
                    :data-sac-id="cell.sacId"
                    :data-teacher-id="rowData.lecturer.teacherId"
                    :data-date="cell.dateStr"
                    @click="handleCellClickBySacId(cell.sacId, rowData.lecturer, $event)">
                  </td>
                </template>
              </tr>
            </tbody>
          </table>

          <!-- 加载状态 -->
          <div v-if="loading" class="loading-indicator">
            <i class="el-icon-loading"></i> 加载中...
          </div>
        </div>
      </div>
    </div>

    <!-- 分页和查询条件 -->
    <div class="pagination-wrapper">
      <div class="pagination-inner">
        <div class="filter-section">
          <el-select v-model="selectedTeacherClass" multiple size="small" placeholder="请选择讲师类型" clearable style="width: 180px;" @change="handleFilterChange">
            <el-option v-for="item in teacherClassList" :key="item.atcId" :label="item.atcName" :value="item.atcId" />
          </el-select>
          <el-button class="ai-btn secondary" size="small" style="margin-left: 10px;" @click="openAuthDialog">
            <i class="el-icon-setting"></i> 权限列
          </el-button>
        </div>
        <el-pagination background
          layout="sizes, prev, pager, next, total"
          :total="totalCount"
          :page-size="pageSize"
          :page-sizes="[15,25, 40, 50, 100]"
          :current-page.sync="currentPage"
          :pager-count="5"
          size="small"
          @size-change="handleSizeChange"
          @current-change="handlePageChange">
        </el-pagination>
      </div>
    </div>

    <!-- 单元格详情弹窗 -->
    <el-dialog :title="dialogType === 'add' ? '新增讲师日程' : '编辑讲师日程'" :visible.sync="cellDialogVisible" width="45%" append-to-body class="schedule-dialog">
      <div class="dialog-header-info" v-if="selectedCell">
        <div class="lecturer-info-mini">
          <div class="lecturer-avatar-mini">{{ getAvatarText(selectedCell.lecturer.teacherName) }}</div>
          <div class="lecturer-name-mini">{{ selectedCell.lecturer.teacherName }}</div>
        </div>
        <div class="date-info">
          <span class="date-text">{{ selectedCell.date.year }}-{{ selectedCell.date.month }}-{{ selectedCell.date.day }}</span>
          <span class="week-text">{{ selectedCell.date.weekDes }}</span>
        </div>
      </div>
      <el-form ref="scheduleFormRef" :model="scheduleForm" :rules="scheduleRules" label-width="100px" class="schedule-form">
        <el-form-item label="日程类型" prop="atstId">
          <el-select v-model="scheduleForm.atstId" placeholder="请选择日程类型" style="width: 100%;">
            <el-option v-for="type in scheduleTypeList" :key="type.atstId" :label="type.atstName" :value="type.atstId">
              <span class="type-dot" :style="{ backgroundColor: type.atstColor }"></span>
              {{ type.atstName }}
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="客户名称" prop="trainCustomerName">
          <el-input v-model="scheduleForm.trainCustomerName" placeholder="请输入客户名称" />
        </el-form-item>
        <el-form-item label="课程名称" prop="trainCourseName">
          <el-input v-model="scheduleForm.trainCourseName" placeholder="请输入课程名称" />
        </el-form-item>
        <el-form-item label="培训时长">
          <el-input v-model="scheduleForm.trainTimeLength" placeholder="请输入培训时长" />
        </el-form-item>
        <el-form-item label="培训形式">
          <el-radio-group v-model="scheduleForm.trainForm">
            <el-radio :label="1">线下</el-radio>
            <el-radio :label="2">线上</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="开始时间" prop="trainStart" label-width="100px">
              <el-date-picker v-model="scheduleForm.trainStart" type="date" placeholder="选择开始时间" style="width: 100%;"
                value-format="yyyy-MM-dd" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="结束时间" prop="trainEnd" label-width="100px">
              <el-date-picker v-model="scheduleForm.trainEnd" type="date" placeholder="选择结束时间" style="width: 100%;"
                value-format="yyyy-MM-dd" :default-value="trainEndDefaultDate" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="授课对象">
          <el-input v-model="scheduleForm.trainObject" placeholder="请输入授课对象" />
        </el-form-item>
        <el-form-item label="培训地点">
          <el-input v-model="scheduleForm.trainAddress" placeholder="请输入培训地点" />
        </el-form-item>
        <el-form-item label="培训需求">
          <el-input v-model="scheduleForm.trainDemand" type="textarea" :rows="3" placeholder="请输入培训需求" />
        </el-form-item>
        <el-form-item label="支付状态">
          <el-radio-group v-model="scheduleForm.trainPayStatus">
            <el-radio :label="1">未开票</el-radio>
            <el-radio :label="2">已开票未支付</el-radio>
            <el-radio :label="3">已支付</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button v-if="dialogType === 'edit'" type="danger" :loading="deleteLoading" @click="handleDelete">
          删除
        </el-button>
        <el-button @click="cellDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="dialogLoading" @click="handleSubmit">
          {{ dialogType === 'add' ? '新增' : '保存' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 表头编辑弹窗 -->
    <el-dialog :title="headerEditType === 'month' ? '编辑月提示' : '编辑星期描述'" :visible.sync="headerDialogVisible" width="400px" append-to-body class="header-edit-dialog">
      <el-form ref="headerFormRef" :model="headerForm" label-width="100px">
        <el-form-item v-if="headerEditType === 'month'" label="月提示" prop="sacDesMonth">
          <el-input v-model="headerForm.sacDesMonth" placeholder="请输入月提示" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item v-else label="星期描述" prop="sacWeekDes">
          <el-input v-model="headerForm.sacWeekDes" placeholder="请输入星期描述" maxlength="50" show-word-limit />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="headerDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="headerDialogLoading" @click="handleHeaderSubmit">
          保存
        </el-button>
      </span>
    </el-dialog>

    <!-- 权限列设置弹窗 -->
    <el-dialog title="权限列设置" :visible.sync="authDialogVisible" width="520px" append-to-body class="auth-dialog" :before-close="handleAuthDialogClose">
      <div class="auth-dialog-content">
        <div class="auth-tip">
          <i class="el-icon-info"></i>
          <span>通过开关控制表格特殊列的显示与隐藏</span>
        </div>
        <el-table :data="authList" border class="auth-table" header-cell-class-name="auth-table-header">
          <el-table-column prop="atsauTypeName" label="列类型" min-width="180" align="center">
            <template slot-scope="scope">
              <span class="auth-type-name">{{ scope.row.atsauTypeName }}</span>
            </template>
          </el-table-column>
          <el-table-column label="显示/隐藏" width="140" align="center">
            <template slot-scope="scope">
              <el-switch
                v-model="scope.row.atsauShowHide"
                :active-value="1"
                :inactive-value="2"
                active-text="显示"
                inactive-text="隐藏"
                @change="handleAuthToggle(scope.row)">
              </el-switch>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getSysAjCalendarYearSchedule, getAjTeacherListOperation, getAjTeacherScheduleTypeList, addAjTeacherSchedule, updateAjTeacherSchedule, deleteAjTeacherSchedule, getAjTeacherScheduleDetails, getAjTeacherScheduleYear, updateSysAjCalendarSchedule, getAjTeacherClassList, getAjTeacherScheduleAuthUserMine, updateAjTeacherScheduleAuthUser } from "../../api/api";

export default {
  name: "YearView",
  props: {
    teacherId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      loading: false,
      calendarData: [], // 年度日历数据

      // 讲师数据
      lecturers: [],
      allLecturers: [], // 全部讲师数据
      totalCount: 0,
      currentPage: 1,
      pageSize: 15,
      searchKeyword: '', // 搜索关键词

      // 讲师行程数据（按讲师id分组，每个讲师对应行程列表）
      scheduleData: {}, // { teacherId: [schedule1, schedule2, ...] }
      // 用于快速查找某个单元格(sacId, teacherId)对应的日程
      scheduleMap: {}, // { "${teacherId}-${sacId}": schedule }
      // 讲师行程合并单元格列表
      scheduleMerges: {}, // { teacherId: [{ startSacId, endSacId, schedule, isFirst }] }

      // 滚动懒加载相关
      scrollTop: 0,
      scrollLeft: 0,
      visibleRowStart: 0,
      visibleRowEnd: 20, // 初始显示20行
      visibleColStart: 0,
      visibleColEnd: 31, // 初始显示31列（一个月）

      // 行高和列宽配置
      rowHeight: 36,
      dayCellWidth: 28,
      specialCellWidth: 60, // 特殊列宽度（sacType >= 1）

      // 弹窗
      cellDialogVisible: false,
      selectedCell: null,
      dialogType: 'add', // 'add' 新增, 'edit' 编辑
      dialogLoading: false,
      deleteLoading: false,

      // 表单数据
      scheduleForm: {
        atsId: null,
        teacherId: null,
        sacId: null,
        atstId: null,
        trainCustomerName: '',
        trainCourseName: '',
        trainTimeLength: '',
        trainStart: '',
        trainEnd: '',
        trainObject: '',
        trainAddress: '',
        trainDemand: '',
        trainForm: 1,
        trainPayStatus: 1
      },
      // 结束时间默认显示的日期（用于同步开始时间的年月）
      trainEndDefaultDate: null,
      // 表单验证规则
      scheduleRules: {
        atstId: [{ required: true, message: '请选择日程类型', trigger: 'change' }],
        trainCustomerName: [{ required: true, message: '请输入客户名称', trigger: 'blur' }],
        trainCourseName: [{ required: true, message: '请输入课程名称', trigger: 'blur' }],
        trainStart: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
        trainEnd: [{ required: true, message: '请选择结束时间', trigger: 'change' }]
      },
      scheduleFormRef: null,

      // 缓存
      monthHeaders: [],
      dayHeaders: [],
      sacIdToIndex: {}, // sacId 到 dayIndex 的映射

      // 行程类型
      scheduleTypeList: [], // 行程类型列表

      // 讲师类型筛选
      teacherClassList: [], // 讲师类型列表
      selectedTeacherClass: [], // 选中的讲师类型

      // 表头编辑弹窗
      headerDialogVisible: false,
      headerDialogLoading: false,
      headerEditType: '', // 'month' 或 'week'
      headerForm: {
        sacId: null,
        sacDesMonth: '',
        sacWeekDes: ''
      },
      headerFormRef: null,

      // 权限列设置弹窗
      authDialogVisible: false,
      authList: [], // 权限列列表
      authListCopy: [], // 权限列原始数据副本，用于比较是否有修改
    };
  },
  computed: {
    totalDays() {
      // 计算一年总天数
      const isLeapYear = (this.currentYear % 4 === 0 && this.currentYear % 100 !== 0) || (this.currentYear % 400 === 0);
      return isLeapYear ? 366 : 365;
    },

    totalTableWidth() {
      // 总宽度 = 固定讲师列 + 所有日期列（特殊列宽度60，普通列28）
      const headerWidth = this.dayHeaders.reduce((sum, day) => {
        return sum + (day.sacType >= 1 ? this.specialCellWidth : this.dayCellWidth);
      }, 0);
      return 160 + headerWidth;
    },

    // 获取可见的日期表头（用于模板渲染）
    visibleDayHeaders() {
      return this.dayHeaders.slice(this.visibleColStart, this.visibleColEnd);
    },

    // 获取可见的讲师（分页）
    visibleLecturers() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.allLecturers;
    },

    // 获取可见的讲师行的单元格数据（分页）
    visibleRowCells() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.lecturerRowCells;
    },

    // 获取普通日期（不含特殊类型，用于第三行星期表头）
    normalDayHeaders() {
      return this.dayHeaders.filter(day => !day.isSpecial);
    },

    // 生成讲师行的单元格渲染数据（处理合并单元格）
    lecturerRowCells() {
      const result = [];
      this.allLecturers.forEach(lecturer => {
        const merges = this.getScheduleMerges(lecturer);
        const cells = [];
        const allSacIds = this.dayHeaders.map(d => d.sacId);

        // 获取所有日期的完整信息（包括sacType）
        const dayHeadersMap = {};
        this.dayHeaders.forEach((day) => {
          dayHeadersMap[day.sacId] = day;
        });

        // 创建已合并的单元格映射（考虑特殊日期）
        const mergedCellMap = {}; // { sacId: { colspan, data, isMerged } }
        merges.forEach(merge => {
          const mergeCells = []; // 这个合并的所有单元格sacId
          let currentSacId = merge.startSacId;

          // 从开始日期遍历到结束日期
          while (currentSacId <= merge.endSacId) {
            const dayInfo = dayHeadersMap[currentSacId];
            if (!dayInfo) {
              currentSacId++;
              continue;
            }

            // 如果是特殊类型(sacType != 0)，合并在此断开
            if (dayInfo.sacType != 0) {
              // 保存之前的合并段
              if (mergeCells.length > 0) {
                const startIdx = allSacIds.indexOf(mergeCells[0]);
                const endIdx = allSacIds.indexOf(mergeCells[mergeCells.length - 1]);
                if (startIdx >= 0 && endIdx >= 0) {
                  const currentColspan = endIdx - startIdx + 1;
                  mergeCells.forEach((sid, i) => {
                    mergedCellMap[sid] = {
                      colspan: i === 0 ? currentColspan : 0,
                      isMerged: true,
                      mergeInfo: merge
                    };
                  });
                }
              }
              // 跳过特殊类型单元格
              currentSacId++;
              mergeCells.length = 0;
              continue;
            }

            mergeCells.push(currentSacId);
            currentSacId++;
          }

          // 保存最后一段合并
          if (mergeCells.length > 0) {
            const startIdx = allSacIds.indexOf(mergeCells[0]);
            const endIdx = allSacIds.indexOf(mergeCells[mergeCells.length - 1]);
            if (startIdx >= 0 && endIdx >= 0) {
              const currentColspan = endIdx - startIdx + 1;
              mergeCells.forEach((sid, i) => {
                mergedCellMap[sid] = {
                  colspan: i === 0 ? currentColspan : 0,
                  isMerged: true,
                  mergeInfo: merge
                };
              });
            }
          }
        });

        // 构建单元格列表 - 只渲染非合并单元格和合并的第一个单元格
        allSacIds.forEach((sacId) => {
          const mergeInfo = mergedCellMap[sacId];
          const dayInfo = dayHeadersMap[sacId];
          // 预计算日期字符串，避免模板中重复计算
          const dateStr = dayInfo ? `${dayInfo.year}-${dayInfo.month}-${dayInfo.day}` : '';

          if (mergeInfo) {
            if (mergeInfo.colspan > 0) { // 只渲染合并的第一个单元格
              cells.push({
                sacId,
                isMerged: true,
                colspan: mergeInfo.colspan,
                mergeInfo: mergeInfo.mergeInfo,
                dateStr,
                lecturer
              });
            }
            // colspan=0 的被合并单元格不渲染
          } else {
            // 检查是否是特殊类型单元格
            if (dayInfo && dayInfo.sacType != 0) {
              // 检查是否有特殊日期记录
              const specialKey = `${lecturer.teacherId}-${sacId}`;
              const specialSchedule = this.scheduleMap[specialKey];

              if (specialSchedule && specialSchedule.isSpecialDay) {
                // 有特殊日期记录，显示 trainTimeLength
                cells.push({
                  sacId,
                  isSpecial: true,
                  isSpecialDay: true,
                  sacType: dayInfo.sacType,
                  colspan: 1,
                  trainTimeLength: specialSchedule.trainTimeLength,
                  displayText: specialSchedule.displayText,
                  atstColor: specialSchedule.atstColor,
                  lecturer
                });
              } else {
                // 没有记录，保持空白
                cells.push({
                  sacId,
                  isSpecial: true,
                  sacType: dayInfo.sacType,
                  colspan: 1,
                  lecturer
                });
              }
            } else {
              cells.push({
                sacId,
                isMerged: false,
                colspan: 1,
                dateStr,
                lecturer
              });
            }
          }
        });

        result.push({
          lecturer,
          cells
        });
      });
      return result;
    },
  },
  watch: {
    currentYear: {
      handler(val) {
        this.fetchCalendarData(val);
      },
      immediate: true
    },
    // 监听开始时间变化，自动同步结束时间的默认显示年月
    'scheduleForm.trainStart': {
      handler(newVal) {
        if (newVal) {
          // 解析开始时间的年和月，设置结束时间的默认显示
          const parts = newVal.split('-');
          if (parts.length >= 2) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // 月份从0开始
            this.trainEndDefaultDate = new Date(year, month, 1);
          }
        }
      }
    }
  },
  mounted() {
    // 初始化讲师数据
    this.initLecturers();
    // 获取行程类型列表
    this.fetchScheduleTypeList();
    // 获取讲师类型列表
    this.fetchTeacherClassList();
  },
  methods: {
    // 获取单元格宽度
    getCellWidth(sacType) {
      return sacType >= 1 ? this.specialCellWidth : this.dayCellWidth;
    },

    // 获取年度日历表头数据
    async fetchCalendarData(year) {
      this.loading = true;
      try {
        const res = await getSysAjCalendarYearSchedule({ sacYear: year });
        if (res.status === 200 && res.result === 'success') {
          this.calendarData = res.data || [];
          this.processCalendarData();
        } else {
          this.$message.error(res.msg || '获取日历数据失败');
        }
      } catch (error) {
        console.error('获取日历数据失败:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理日历数据，生成表头
    processCalendarData() {
      this.monthHeaders = [];
      this.dayHeaders = [];
      this.sacIdToIndex = {};

      this.calendarData.forEach((monthItem) => {
        const children = monthItem.sysAjCalendarChildren || [];

        // 按 sacDay 排序，确保日期按顺序排列
        children.sort((a, b) => (a.sacDay || 0) - (b.sacDay || 0));

        // 普通日期数据 sacType === 0
        const days = children.filter(item => item.sacType === 0);
        // 特殊类型数据 sacType >= 1
        const specialTypes = children.filter(item => item.sacType >= 1);

        if (days.length > 0) {
          // 获取当前月份
          const currentMonth = days[0].sacMonth;

          // 添加月份表头（包含普通日期和特殊类型的总列数）
          this.monthHeaders.push({
            label: currentMonth + '月',
            days: days.length + specialTypes.length
          });

          // 添加日期表头
          days.forEach((day) => {
            const globalIndex = this.dayHeaders.length;
            this.dayHeaders.push({
              sacId: day.sacId,
              year: day.sacYear,
              sacType: day.sacType,
              month: day.sacMonth,
              day: day.sacDay,
              weekDay: day.sacWeek,
              weekDes: day.sacWeekDes,
              isSpecial: false
            });
            this.sacIdToIndex[day.sacId] = globalIndex;
          });

          // 添加特殊类型表头（排列在日期之后）
          specialTypes.forEach((item) => {
            const globalIndex = this.dayHeaders.length;
            this.dayHeaders.push({
              sacId: item.sacId,
              year: item.sacYear,
              month: item.sacMonth,
              day: 0,
              weekDay: 0,
              weekDes: item.sacWeekDes,
              sacType: item.sacType,
              isSpecial: true
            });
            this.sacIdToIndex[item.sacId] = globalIndex;
          });
        }
      });

      // 更新可见列
      this.updateVisibleCol();
    },

    // 初始化讲师数据
    async initLecturers() {
      try {
        const params = {
          currentPage: this.currentPage,
          pageSize: this.pageSize,
          keyword: this.searchKeyword || ''
        };
        // 添加讲师类型筛选条件
        if (this.selectedTeacherClass) {
        const atcIds = JSON.parse(JSON.stringify(this.selectedTeacherClass))
          params.atcIds = JSON.stringify(atcIds);
        }
        this.$loading.show()
        const res = await getAjTeacherListOperation(params);
        if (res.status === 200) {
          // 接口返回格式：res.data 是数组，res.total 是总数
          this.allLecturers = res.data || [];
          this.totalCount = res.total || 0;
          // 获取讲师行程数据
          await this.fetchTeacherScheduleYear();
            this.$loading.close()
        } else {
          this.$message.error(res.msg || '获取讲师列表失败');
          this.$loading.close()
        }
      } catch (error) {
        console.error('获取讲师列表失败:', error);
      }
    },

    // 获取讲师年度行程数据
    async fetchTeacherScheduleYear() {
      try {
        // 获取当前年份和讲师ID列表
        const year = this.currentYear;
        const teacherIds = this.allLecturers.map(t => t.teacherId);
        const params = {
          sacYear: year,
          currentPage: this.currentPage,
          pageSize: this.pageSize,
          teacherIds: JSON.stringify(teacherIds)
        };
        const res = await getAjTeacherScheduleYear(params);
        if (res.status === 200 && res.data) {
          // 处理返回数据，构建查询结构
          this.processScheduleData(res.data);
        } else {
          console.error('获取讲师行程数据失败:', res.msg);
        }
      } catch (error) {
        console.error('获取讲师行程数据失败:', error);
      }
    },

    // 处理讲师行程数据，构建快速查询结构
    processScheduleData(data) {
      // 清空旧数据
      this.scheduleData = {};
      this.scheduleMap = {};
      this.scheduleMerges = {};

      if (!Array.isArray(data)) {
        console.warn('行程数据不是数组:', data);
        return;
      }


      data.forEach(teacherItem => {
        const teacherId = teacherItem.teacherId;
        const scheduleList = teacherItem.ajTeacherScheduleList || [];

        // 存储讲师行程列表
        this.scheduleData[teacherId] = scheduleList;
        this.scheduleMerges[teacherId] = [];

        // 只处理有实际内容的日程（trainCustomerName不为空的）
        const validSchedules = scheduleList.filter(s => s.trainCustomerName && s.trainStartId && s.trainEndId);

        // 按sacId范围排序（先按开始时间，再按结束时间）
        validSchedules.sort((a, b) => {
          if (a.trainStartId !== b.trainStartId) {
            return a.trainStartId - b.trainStartId;
          }
          return a.trainEndId - b.trainEndId;
        });

        // 计算每个日程未被覆盖的日期范围
        // 使用一个变量记录已被覆盖的最大sacId
        let maxCoveredSacId = -1;

        validSchedules.forEach(schedule => {
          const startSacId = schedule.trainStartId;
          const endSacId = schedule.trainEndId;

          if (startSacId > endSacId) {
            // 异常情况，跳过
            return;
          }

          // 计算未被覆盖的日期范围
          let visibleStart = startSacId;
          let visibleEnd = endSacId;

          if (maxCoveredSacId >= startSacId) {
            // 起始部分被覆盖
            visibleStart = maxCoveredSacId + 1;
          }

          if (visibleStart <= endSacId) {
            // 至少有一部分未被覆盖
            const visibleSchedule = {
              ...schedule,
              _visibleStartId: visibleStart,
              _visibleEndId: visibleEnd
            };

            // 保存合并单元格信息（使用可见范围）
            this.scheduleMerges[teacherId].push({
              startSacId: visibleStart,
              endSacId: visibleEnd,
              schedule: visibleSchedule,
              isFirst: true,
              // 记录原始范围用于调试
              originalStart: startSacId,
              originalEnd: endSacId
            });

            // 构建 scheduleMap（遍历可见的每一天）
            for (let sacId = visibleStart; sacId <= visibleEnd; sacId++) {
              const key = `${teacherId}-${sacId}`;
              const isFirst = sacId === visibleStart;
              this.scheduleMap[key] = {
                ...visibleSchedule,
                isFirstCell: isFirst
              };
            }

            // 更新最大覆盖sacId
            maxCoveredSacId = Math.max(maxCoveredSacId, endSacId);
          } else {
            // 完全被覆盖，不添加任何内容
          }
        });

        // 处理特殊日期记录（只有trainStartId，没有trainEndId）
        // 这类记录关联到特殊列（如"拜访调研跟学"），取trainTimeLength字段显示
        const specialDaySchedules = scheduleList.filter(s =>
          !s.trainCustomerName &&
          s.trainStartId &&
          !s.trainEndId &&
          s.trainTimeLength !== null &&
          s.trainTimeLength !== undefined
        );
        specialDaySchedules.forEach(schedule => {
          const sacId = schedule.trainStartId;
          const key = `${teacherId}-${sacId}`;
          // 检查是否已被普通日程占用
          if (!this.scheduleMap[key]) {
            // 如果没有被占用，添加特殊日期记录
            this.scheduleMap[key] = {
              ...schedule,
              isFirstCell: true,
              isSpecialDay: true,
              // 显示内容使用 trainTimeLength
              displayText: String(schedule.trainTimeLength || 0)
            };
          }
        });
      });
    },

    // 获取讲师某行的合并单元格列表
    getScheduleMerges(lecturer) {
      return this.scheduleMerges[lecturer.teacherId] || [];
    },

    // 获取行程类型列表
    async fetchScheduleTypeList() {
      try {
        const res = await getAjTeacherScheduleTypeList();
        if (res.status === 200) {
          this.scheduleTypeList = res.data || [];
        } else {
          this.$message.error(res.msg || '获取行程类型失败');
        }
      } catch (error) {
        console.error('获取行程类型失败:', error);
      }
    },

    // 获取讲师类型列表
    async fetchTeacherClassList() {
      try {
        const res = await getAjTeacherClassList();
        if (res.status === 200) {
          this.teacherClassList = res.data || [];
        } else {
          this.$message.error(res.msg || '获取讲师类型失败');
        }
      } catch (error) {
        console.error('获取讲师类型失败:', error);
      }
    },

    // 处理滚动 - 懒加载
    handleScroll(e) {
      const { scrollTop, scrollLeft } = e.target;
      this.scrollTop = scrollTop;
      this.scrollLeft = scrollLeft;

      // 更新表头位置
      if (this.$refs.headerContainer) {
        this.$refs.headerContainer.scrollLeft = scrollLeft;
      }

      // 计算可见列
      this.updateVisibleCol();
    },

    // 更新可见列
    updateVisibleCol() {
      const containerWidth = this.$refs.tableWrapper?.clientWidth || 1200;
      const startCol = Math.floor((this.scrollLeft - 160) / this.dayCellWidth); // 减去固定列宽度
      const buffer = 3; // 缓冲列数
      const endCol = Math.min(startCol + Math.ceil(containerWidth / this.dayCellWidth) + buffer, this.dayHeaders.length);

      this.visibleColStart = Math.max(0, startCol - buffer);
      this.visibleColEnd = Math.max(this.visibleColStart + 1, endCol);
    },

    // 切换年份
    changeYear(delta) {
      this.currentYear += delta;
      this.visibleColStart = 0;
      this.visibleColEnd = 31;
    },

    // 分页切换
    handlePageChange(page) {
      this.currentPage = page;
      // 滚动到顶部
      if (this.$refs.bodyContainer) {
        this.$refs.bodyContainer.scrollTop = 0;
      }
      // 重新获取讲师数据
      this.initLecturers();
    },

    // 每页条数切换
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1; // 重置到第一页
      // 滚动到顶部
      if (this.$refs.bodyContainer) {
        this.$refs.bodyContainer.scrollTop = 0;
      }
      // 重新获取讲师数据
      this.initLecturers();
    },

    // 讲师类型筛选变化
    handleFilterChange() {
      this.currentPage = 1; // 重置到第一页
      // 滚动到顶部
      if (this.$refs.bodyContainer) {
        this.$refs.bodyContainer.scrollTop = 0;
      }
      // 重新获取讲师数据
      this.initLecturers();
    },

    // 判断是否是今天
    isToday(year, month, day) {
      const today = new Date();
      return year === today.getFullYear() && month === today.getMonth() + 1 && day === today.getDate();
    },

    // 获取单元格样式
    getCellStyle(sacId, teacherId) {
      // 查找对应的day信息
      const day = this.dayHeaders.find(d => d.sacId == sacId);
      // 如果是特殊类型或普通单元格，直接返回空样式
      if (!day || day.sacType != 0) {
        return {};
      }

      // 确保 sacId 和 teacherId 是字符串类型，用于匹配
      const key = `${String(teacherId)}-${String(sacId)}`;
      const schedule = this.scheduleMap[key];

      if (!schedule) {
        return {};
      }

      // 返回背景色（取atstColor字段）
      return {
        backgroundColor: schedule.atstColor || '#409eff'
      };
    },

    // 获取单元格内容
    getCellContent(sacId, teacherId) {
      const key = `${String(teacherId)}-${String(sacId)}`;
      const schedule = this.scheduleMap[key];

      if (!schedule || !schedule.isFirstCell) {
        return '';
      }

      // 只在第一个单元格显示文字
      return `<span class="schedule-text" title="${schedule.trainCustomerName || ''}">${schedule.trainCustomerName || ''}</span>`;
    },

    // 点击单元格
    handleCellClick(day, lecturer, event) {
      if (day.sacType != 0) return;
      this.handleCellClickBySacId(day.sacId, lecturer, event);
    },

    // 根据sacId点击单元格
    handleCellClickBySacId(sacId, lecturer, event) {
      // 使用缓存快速查找day信息
      const index = this.sacIdToIndex[sacId];
      const day = index !== undefined ? this.dayHeaders[index] : null;
      if (!day || day.sacType != 0) return;

      const key = `${String(lecturer.teacherId)}-${String(sacId)}`;
      const schedule = this.scheduleMap[key] || null;

      this.selectedCell = {
        date: day,
        lecturer: lecturer,
        schedule: schedule
      };

      // 判断是新增还是编辑
      if (schedule && schedule.atsId) {
        // 有atsId且有文本内容为编辑
        this.dialogType = 'edit';
        this.loadScheduleDetail(schedule.atsId);
      } else {
        // 没有atsId或没有文本内容为新增
        this.dialogType = 'add';
        this.resetForm();
        this.scheduleForm.teacherId = lecturer.teacherId;
        this.scheduleForm.sacId = sacId;
        // 默认开始时间为选中单元格的日期
        const defaultDate = `${day.year}-${String(day.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
        this.scheduleForm.trainStart = defaultDate;
        // 设置结束时间的默认显示年月与开始时间相同
        this.trainEndDefaultDate = new Date(day.year, day.month - 1, 1);
      }

      this.cellDialogVisible = true;

      // 触发事件
      this.$emit('cell-click', {
        sacId,
        teacherId: lecturer.teacherId,
        date: day,
        lecturer,
        schedule
      });
    },

    // 根据sacId快速获取日期信息（已废弃，模板中直接使用预计算的dateStr）
    getCellDate(sacId) {
      const index = this.sacIdToIndex[sacId];
      if (index !== undefined) {
        const day = this.dayHeaders[index];
        if (day) {
          return `${day.year}-${day.month}-${day.day}`;
        }
      }
      return '';
    },

    // 加载日程详情
    async loadScheduleDetail(atsId) {
      try {
        const res = await getAjTeacherScheduleDetails({ atsId });
        if (res.status === 200 && res.data) {
          const data = res.data;
          this.scheduleForm = {
            atsId: data.atsId,
            teacherId: data.teacherId,
            sacId: data.sacId,
            atstId: data.atstId,
            trainCustomerName: data.trainCustomerName || '',
            trainCourseName: data.trainCourseName || '',
            trainTimeLength: data.trainTimeLength || '',
            trainStart: data.trainStart || '',
            trainEnd: data.trainEnd || '',
            trainObject: data.trainObject || '',
            trainAddress: data.trainAddress || '',
            trainDemand: data.trainDemand || '',
            trainForm: data.trainForm || 1,
            trainPayStatus: data.trainPayStatus || 1
          };
        }
      } catch (error) {
        console.error('获取日程详情失败:', error);
      }
    },

    // 重置表单
    resetForm() {
      this.scheduleForm = {
        atsId: null,
        teacherId: null,
        sacId: null,
        atstId: null,
        trainCustomerName: '',
        trainCourseName: '',
        trainTimeLength: '',
        trainStart: '',
        trainEnd: '',
        trainObject: '',
        trainAddress: '',
        trainDemand: '',
        trainForm: 1,
        trainPayStatus: 1
      };
      if (this.$refs.scheduleFormRef) {
        this.$refs.scheduleFormRef.resetFields();
      }
    },

    // 提交表单
    async handleSubmit() {
      if (!this.$refs.scheduleFormRef) return;

      try {
        await new Promise((resolve, reject) => {
          this.$refs.scheduleFormRef.validate((valid) => {
            if (valid) resolve();
            else reject(new Error('验证失败'));
          });
        });

        // 确认提交
        const confirmMsg = this.dialogType === 'edit' ? '确定要保存修改吗？' : '确定要新增该日程吗？';
        await this.$confirm(confirmMsg, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        this.dialogLoading = true;

        const params = { ...this.scheduleForm };
        let res;
        if (this.dialogType === 'edit') {
          res = await updateAjTeacherSchedule(params);
        } else {
          res = await addAjTeacherSchedule(params);
        }

        if (res.status === 200 && res.data) {
          this.$message.success(this.dialogType === 'edit' ? '编辑成功' : '新增成功');
          this.cellDialogVisible = false;
          // 使用返回的数据更新本地状态
          this.updateLocalSchedule(res.data);
        } else {
          this.$message.error(res.msg || '操作失败');
        }
      } catch (error) {
        if (error !== 'cancel' && error.message !== '验证失败') {
          console.error('提交失败:', error);
          this.$message.error('操作失败');
        }
      } finally {
        this.dialogLoading = false;
      }
    },

    // 删除日程
    async handleDelete() {
      if (!this.scheduleForm.atsId) return;

      try {
        await this.$confirm('确定要删除该日程吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        this.deleteLoading = true;
        const res = await deleteAjTeacherSchedule({ atsId: this.scheduleForm.atsId });

        if (res.status === 200) {
          this.$message.success('删除成功');
          this.cellDialogVisible = false;
          // 从本地数据中删除
          this.deleteLocalSchedule(this.scheduleForm.atsId, this.scheduleForm.teacherId);
        } else {
          this.$message.error(res.msg || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          this.$message.error('删除失败');
        }
      } finally {
        this.deleteLoading = false;
      }
    },

    // 从本地删除日程数据
    deleteLocalSchedule(atsId, teacherId) {
      if (!teacherId || !this.scheduleData[teacherId]) return;

      // 删除所有匹配atsId的日程（可能有多个不同sacId范围的）
      const newList = this.scheduleData[teacherId].filter(s => s.atsId !== atsId);
      this.$set(this.scheduleData, teacherId, newList);

      // 重新处理该讲师的行程数据
      this.reprocessTeacherSchedule(teacherId);

      // 强制刷新视图
      this.$forceUpdate();
    },

    // 更新本地日程数据（模块化更新）- 支持单个对象或数组
    updateLocalSchedule(scheduleData) {
      if (!scheduleData) return;

      // 统一转为数组处理
      const dataList = Array.isArray(scheduleData) ? scheduleData : [scheduleData];
      const teacherIds = new Set();

      dataList.forEach(data => {
        if (!data || !data.teacherId || !data.trainStartId) {
          console.warn('返回数据缺少必要字段，无法更新本地状态:', data);
          return;
        }

        const teacherId = data.teacherId;
        teacherIds.add(teacherId);

        // 确保 scheduleData[teacherId] 存在且是响应式数组
        if (!this.scheduleData[teacherId]) {
          this.$set(this.scheduleData, teacherId, []);
        }

        // 编辑时：通过 atsId 查找已存在的日程并更新（忽略日期范围变化）
        // 新增时：没有 atsId 或找不到匹配的记录
        let existingIndex = -1;
        if (data.atsId) {
          existingIndex = this.scheduleData[teacherId].findIndex(s => s.atsId === data.atsId);
        }

        if (existingIndex >= 0) {
          // 更新现有日程 - 完全替换旧记录（支持日期范围变化）
          this.$set(this.scheduleData[teacherId], existingIndex, data);
        } else {
          // 新增日程 - 使用数组切片保持响应式
          const newList = this.scheduleData[teacherId].slice();
          newList.push(data);
          this.$set(this.scheduleData, teacherId, newList);
        }
      });

      // 重新处理所有受影响的讲师行程数据
      teacherIds.forEach(teacherId => {
        this.reprocessTeacherSchedule(teacherId);
      });

      // 强制刷新视图
      this.$forceUpdate();
    },

    // 重新处理单个讲师的行程数据
    reprocessTeacherSchedule(teacherId) {
      const scheduleList = this.scheduleData[teacherId] || [];

      // 使用 Vue.set 确保响应式更新
      this.$set(this.scheduleMerges, teacherId, []);

      // 清理该讲师的旧map数据
      const keysToDelete = [];
      Object.keys(this.scheduleMap).forEach(key => {
        if (key.startsWith(`${teacherId}-`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => {
        this.$delete(this.scheduleMap, key);
      });

      // 只处理有实际内容的日程
      const validSchedules = scheduleList.filter(s => s.trainCustomerName && s.trainStartId && s.trainEndId);

      // 按sacId范围排序
      validSchedules.sort((a, b) => {
        if (a.trainStartId !== b.trainStartId) {
          return a.trainStartId - b.trainStartId;
        }
        return a.trainEndId - b.trainEndId;
      });

      // 计算每个日程未被覆盖的日期范围
      let maxCoveredSacId = -1;

      validSchedules.forEach(schedule => {
        const sStart = schedule.trainStartId;
        const sEnd = schedule.trainEndId;

        if (sStart > sEnd) return;

        let visibleStart = sStart;
        let visibleEnd = sEnd;

        if (maxCoveredSacId >= sStart) {
          visibleStart = maxCoveredSacId + 1;
        }

        if (visibleStart <= sEnd) {
          const visibleSchedule = {
            ...schedule,
            _visibleStartId: visibleStart,
            _visibleEndId: visibleEnd
          };

          // 获取当前合并列表并添加新项
          const currentMerges = this.scheduleMerges[teacherId] || [];
          currentMerges.push({
            startSacId: visibleStart,
            endSacId: visibleEnd,
            schedule: visibleSchedule,
            isFirst: true
          });
          this.$set(this.scheduleMerges, teacherId, currentMerges);

          for (let sacId = visibleStart; sacId <= visibleEnd; sacId++) {
            const key = `${teacherId}-${sacId}`;
            const isFirst = sacId === visibleStart;
            this.$set(this.scheduleMap, key, {
              ...visibleSchedule,
              isFirstCell: isFirst
            });
          }

          maxCoveredSacId = Math.max(maxCoveredSacId, sEnd);
        }
      });

      // 处理特殊日期记录
      const specialDaySchedules = scheduleList.filter(s =>
        !s.trainCustomerName &&
        s.trainStartId &&
        !s.trainEndId &&
        s.trainTimeLength !== null &&
        s.trainTimeLength !== undefined
      );
      specialDaySchedules.forEach(schedule => {
        const sacId = schedule.trainStartId;
        const key = `${teacherId}-${sacId}`;
        if (!this.scheduleMap[key]) {
          this.$set(this.scheduleMap, key, {
            ...schedule,
            isFirstCell: true,
            isSpecialDay: true,
            displayText: String(schedule.trainTimeLength || 0)
          });
        }
      });
    },

    // 编辑日程
    handleEditSchedule() {
      if (this.selectedCell) {
        this.$emit('edit-schedule', this.selectedCell);
      }
    },

    // 获取头像文字
    getAvatarText(name) {
      if (!name) return '讲师';
      const len = name.length;
      if (len <= 2) return name;
      return name.substring(len - 2);
    },

    // 点击月份表头
    handleMonthHeaderClick(monthIndex) {
      const month = this.monthHeaders[monthIndex];
      if (!month) return;

      // 获取该月份第一个普通日期的sacId
      const monthSacIds = this.dayHeaders.filter(day =>
        day.month === monthIndex + 1 && day.sacType === 0
      );

      if (monthSacIds.length === 0) return;

      // 使用该月份第一个普通日期的sacId
      const sacId = monthSacIds[0].sacId;

      this.headerEditType = 'month';
      this.headerForm = {
        sacId: sacId,
        sacDesMonth: month.label || '',
        sacWeekDes: ''
      };
      this.headerDialogVisible = true;
    },

    // 点击星期表头
    handleWeekHeaderClick(day) {
      // 如果sacType大于1，不弹框
      if (day.sacType > 0) return;

      this.headerEditType = 'week';
      this.headerForm = {
        sacId: day.sacId,
        sacDesMonth: '',
        sacWeekDes: day.weekDes || ''
      };
      this.headerDialogVisible = true;
    },

    // 提交表头编辑
    async handleHeaderSubmit() {
      try {
        this.headerDialogLoading = true;

        const params = {
          sacId: this.headerForm.sacId
        };

        if (this.headerEditType === 'month') {
          params.sacDesMonth = this.headerForm.sacDesMonth;
        } else {
          params.sacWeekDes = this.headerForm.sacWeekDes;
        }

        const res = await updateSysAjCalendarSchedule(params);

        if (res.status === 200 && res.result === 'success') {
          this.$message.success('保存成功');
          this.headerDialogVisible = false;
          // 更新本地数据
          this.updateLocalHeaderData();
        } else {
          this.$message.error(res.msg || '保存失败');
        }
      } catch (error) {
        console.error('保存表头失败:', error);
        this.$message.error('保存失败');
      } finally {
        this.headerDialogLoading = false;
      }
    },

    // 更新本地表头数据
    updateLocalHeaderData() {
      const sacId = this.headerForm.sacId;
      const index = this.sacIdToIndex[sacId];

      if (index === undefined) return;

      if (this.headerEditType === 'month') {
        // 更新月份显示
        const day = this.dayHeaders[index];
        if (day) {
          // 找到该月份的所有日期，更新monthHeaders的label
          const monthNum = day.month;
          const monthHeader = this.monthHeaders.find(m => m.label === `${monthNum}月` || m.label === this.headerForm.sacDesMonth);
          if (monthHeader) {
            monthHeader.label = this.headerForm.sacDesMonth || `${monthNum}月`;
          }
        }
      } else {
        // 更新星期描述
        if (this.dayHeaders[index]) {
          this.$set(this.dayHeaders[index], 'weekDes', this.headerForm.sacWeekDes);
        }
      }
    },

    // 打开权限列设置弹窗
    async openAuthDialog() {
      try {
        const res = await getAjTeacherScheduleAuthUserMine();
        if (res.status === 200 && res.data) {
          // 保存原始数据副本，用于比较是否有修改
          this.authListCopy = JSON.parse(JSON.stringify(res.data));
          this.authList = res.data;
          this.authDialogVisible = true;
        } else {
          this.$message.error(res.msg || '获取权限列数据失败');
        }
      } catch (error) {
        console.error('获取权限列数据失败:', error);
        this.$message.error('获取权限列数据失败');
      }
    },

    // 权限列是否有修改
    hasAuthModified() {
      if (this.authList.length !== this.authListCopy.length) return true;
      for (let i = 0; i < this.authList.length; i++) {
        if (this.authList[i].atsauShowHide !== this.authListCopy[i].atsauShowHide) {
          return true;
        }
      }
      return false;
    },

    // 权限列弹窗关闭处理
    handleAuthDialogClose(done) {
      if (this.hasAuthModified()) {
        this.$confirm('权限设置已修改，是否刷新表格以应用更改？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(async () => {
          // 先关闭弹窗
          this.authDialogVisible = false;
          // 刷新表格数据
          await this.refreshTableData();
          if (done) done();
        }).catch(() => {
          // 取消则正常关闭
          this.authDialogVisible = false;
          if (done) done();
        });
      } else {
        this.authDialogVisible = false;
        if (done) done();
      }
    },

    // 刷新表格数据
    async refreshTableData() {
      this.loading = true;
      try {
        // 重新获取日历数据
        await this.fetchCalendarData(this.currentYear);
        // 重新获取讲师数据
        await this.initLecturers();
      } finally {
        this.loading = false;
      }
    },

    // 切换权限列显示/隐藏
    async handleAuthToggle(row) {
      try {
        const params = {
          atsauId: row.atsauId,
          atsauShowHide: row.atsauShowHide
        };
        const res = await updateAjTeacherScheduleAuthUser(params);
        if (res.status === 200) {
          this.$message.success(row.atsauShowHide === 1 ? '已设置为显示' : '已设置为隐藏');
        } else {
          // 恢复原状态
          row.atsauShowHide = row.atsauShowHide === 1 ? 2 : 1;
          this.$message.error(res.msg || '设置失败');
        }
      } catch (error) {
        // 恢复原状态
        row.atsauShowHide = row.atsauShowHide === 1 ? 2 : 1;
        console.error('设置权限列失败:', error);
        this.$message.error('设置失败');
      }
    }
  }
};
</script>

<style scoped>
.year-view-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
  padding: 16px 20px;
}

/* 顶部工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.toolbar-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  position: relative;
  padding-left: 12px;
}

.toolbar-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 18px;
  background: linear-gradient(180deg, #409eff 0%, #66b1ff 100%);
  border-radius: 2px;
}

.year-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
  border-radius: 20px;
  border: 1px solid #b3d8ff;
}
.container ::v-deep .el-pagination__sizes {
  padding-top: 5px !important;
}
.container ::v-deep .el-pagination__total {
  padding-top: 5px !important;
}
.year-picker i {
  cursor: pointer;
  padding: 6px;
  color: #409eff;
  font-size: 14px;
  transition: all 0.2s;
  border-radius: 50%;
  background: #fff;
}

.year-picker i:hover {
  background: #409eff;
  color: #fff;
  transform: scale(1.1);
}

.year-value {
  font-size: 20px;
  font-weight: 700;
  color: #409eff;
  min-width: 56px;
  text-align: center;
  letter-spacing: 2px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.toolbar-info {
  font-size: 13px;
  color: #606266;
  background: #f5f7fa;
  padding: 6px 12px;
  border-radius: 4px;
}

.toolbar-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 6px 14px;
  background: #fafafa;
  border-radius: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color-block {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
}

.legend-text {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

/* 表格区域 */
.table-area {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #ebeef5;
}

/* 表格容器 */
.table-wrapper {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 表头容器 */
.table-header-container {
  overflow: hidden;
  flex-shrink: 0;
  background: #fff;
  border-bottom: 2px solid #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.table-header-container table {
  background: #fff;
}

/* 表格样式 */
.year-table {
  border-collapse: collapse;
  table-layout: fixed;
}

.year-table th,
.year-table td {
  border: 1px solid #d4d4d4;
  text-align: center;
  vertical-align: middle;
  font-size: 12px;
}

/* 固定列 */
.fixed-col {
  position: sticky;
  left: 0;
  z-index: 10;
  width: 60px;
  min-width: 60px;
  background: #fff;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.06);
}

.fixed-col.lecturer-col {
  background: linear-gradient(180deg, #f0f7ff 0%, #e6f2ff 100%);
}

.lecturer-col {
  background: linear-gradient(180deg, #f0f7ff 0%, #e6f2ff 100%);
  color: #303133;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 2px;
  border-right: 1px solid #d4e8ff;
}

/* 表头行 */
.header-row {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.header-row th {
  height: 26px;
  line-height: 26px;
  padding: 0;
  font-weight: 600;
  font-size: 12px;
  color: #303133;
}

/* 月份行 */
.month-row {
  height: 32px;
}

.month-row .month-cell {
  background: linear-gradient(180deg, #f0f7ff 0%, #e6f2ff 100%);
  font-weight: 600;
  color: #303133;
  font-size: 12px;
  letter-spacing: 1px;
  border-bottom: 1px solid #d4e8ff;
}

.month-row .month-cell.header-clickable,
.week-row .week-cell.header-clickable {
  cursor: pointer;
  transition: all 0.2s;
}

.month-row .month-cell.header-clickable:hover,
.week-row .week-cell.header-clickable:hover {
  background: linear-gradient(180deg, #d4e8ff 0%, #c0d9f5 100%) !important;
  color: #409eff;
}

/* 日期行（第二行） */
.day-week-row {
  height: 28px;
}

.day-week-row .day-cell {
  font-weight: 600;
  color: #303133;
  font-size: 11px;
  transition: all 0.2s;
  background: #fff;
}

.day-week-row .day-cell.is-today {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.4);
}

.day-week-row .day-cell.is-special {
  background: #f8fafc;
  color: #909399;
  font-weight: 500;
  font-size: 10px;
  border-left: 1px dashed #e4e7ed;
}

/* 星期行（第三行） */
.week-row {
  height: 26px;
  background: #fafafa;
}

.week-row .week-cell {
  font-weight: 500;
  color: #606266;
  font-size: 11px;
  transition: all 0.2s;
  background: #fafafa;
}

.week-row .week-cell.is-today {
  background: linear-gradient(135deg, #ecf5ff 0%, #d4e8ff 100%);
  color: #409eff;
  font-weight: 600;
}

/* 占位单元格 */
.placeholder-cell {
  border: none;
  background: transparent;
}

/* 表格内容区域 */
.table-body-container {
  flex: 1;
  overflow: auto;
  background: #fff;
}

.table-body-container table {
  background: #fff;
}

/* 讲师行 */
.lecturer-row {
  height: 29px;
  transition: all 0.2s ease;
}

.lecturer-row:hover {
  background: linear-gradient(90deg, #f0f7ff 0%, #ecf5ff 100%);
}

.lecturer-row:hover .lecturer-info .lecturer-name {
  color: #409eff;
}

/* 讲师单元格 */
.lecturer-cell {
  padding: 2px 2px;
  text-align: left;
  height: 20px;
  box-sizing: border-box;
  background: #fff;
}

.lecturer-name {
  font-size: 11px;
  color: #303133;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s ease;
}

.lecturer-row:hover .lecturer-name {
  color: #409eff;
}

/* 日程单元格 */
.schedule-cell {
  padding: 2px 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 28px;
  height: 29px;
  vertical-align: middle;
  background: #fff;
  overflow: hidden;
  box-sizing: border-box;
  text-align: center;
}

.schedule-cell .cell-content {
  justify-content: center;
}

.schedule-cell:hover {
  background: linear-gradient(180deg, #ecf5ff 0%, #d4e8ff 100%) !important;
  box-shadow: inset 0 0 0 1px #409eff;
}

.schedule-cell.is-special {
  background: #fafafa;
  cursor: default;
}

.schedule-cell.is-special:hover {
  background: #fafafa;
}

.schedule-cell.is-special.is-special-day {
  background: linear-gradient(180deg, #f0f7ff 0%, #e6f2ff 100%);
}

.cell-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.schedule-text {
  font-size: 11px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  line-height: 1.3;
  min-width: 20px;
  font-weight: 500;
  text-align: center;
  width: 100%;
}

/* 加载指示器 */
.loading-indicator {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, rgba(64, 158, 255, 0.9) 0%, rgba(102, 177, 255, 0.9) 100%);
  color: #fff;
  padding: 10px 24px;
  border-radius: 20px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.4);
}

.loading-indicator i {
  font-size: 16px;
}

/* 分页 */
.pagination-wrapper {
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  margin-top: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.pagination-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-section {
  display: flex;
  align-items: center;
  height: 36px;
}

.filter-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.pagination-wrapper ::v-deep .el-pagination {
  display: flex;
  align-items: center;
  font-weight: normal;
  font-size: 14px;
}

.pagination-wrapper ::v-deep .el-pagination .el-pagination__sizes {
  display: flex;
  align-items: center;
}

.pagination-wrapper ::v-deep .el-pagination .el-pagination__total {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #909399;
}

.pagination-wrapper ::v-deep .el-pagination.is-background .el-pager li {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  margin: 0 4px;
  font-weight: 500;
  min-width: 36px;
  height: 36px;
  line-height: 36px;
  transition: all 0.2s;
}

.pagination-wrapper ::v-deep .el-pagination.is-background .el-pager li:hover {
  color: #409eff;
  border-color: #409eff;
  background: #ecf5ff;
}

.pagination-wrapper ::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: #fff;
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.4);
}

.pagination-wrapper ::v-deep .btn-prev,
.pagination-wrapper ::v-deep .btn-next {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 0 12px;
  min-width: 36px;
  height: 36px;
  color: #606266;
  transition: all 0.2s;
}

.pagination-wrapper ::v-deep .btn-prev:hover,
.pagination-wrapper ::v-deep .btn-next:hover {
  color: #409eff;
  border-color: #409eff;
  background: #ecf5ff;
}

/* 弹窗样式 */
.cell-detail {
  padding: 10px 0;
}

.detail-row {
  display: flex;
  margin-bottom: 12px;
}

.detail-row .label {
  color: #909399;
  width: 80px;
  flex-shrink: 0;
}

.detail-row .value {
  color: #303133;
}

/* 滚动条样式 */
.table-body-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-body-container::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #c0d8ff 0%, #a0c4ff 100%);
  border-radius: 4px;
  transition: all 0.2s;
}

.table-body-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #409eff 0%, #66b1ff 100%);
}

.table-body-container::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 4px;
}

/* 弹窗样式优化 */
.cell-detail {
  padding: 10px 0;
}

.detail-row {
  display: flex;
  margin-bottom: 16px;
  align-items: center;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-row .label {
  color: #909399;
  width: 80px;
  flex-shrink: 0;
  font-size: 14px;
}

.detail-row .value {
  color: #303133;
  font-size: 14px;
  font-weight: 500;
}

/* 弹窗按钮样式优化 */
.dialog-footer ::v-deep .el-button {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
}

.dialog-footer ::v-deep .el-button--primary {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  border: none;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.dialog-footer ::v-deep .el-button--primary:hover {
  background: linear-gradient(135deg, #337ecc 0%, #409eff 100%);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
}

/* 弹窗样式 */
.schedule-dialog ::v-deep .el-dialog {
  border-radius: 12px;
}

.schedule-dialog ::v-deep .el-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.schedule-dialog ::v-deep .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.schedule-dialog ::v-deep .el-dialog__body {
  padding: 20px 24px;
}

.schedule-dialog ::v-deep .el-dialog__footer {
  padding: 16px 24px 20px;
  border-top: 1px solid #f0f0f0;
}

/* 弹窗头部信息 */
.dialog-header-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 8px;
  margin-bottom: 20px;
}

.lecturer-info-mini {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lecturer-avatar-mini {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.lecturer-name-mini {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.date-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.date-text {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.week-text {
  font-size: 12px;
  color: #909399;
}

/* 表单样式 */
.schedule-form {
  padding: 0;
}

.schedule-form ::v-deep .el-form-item {
  margin-bottom: 18px;
}

.schedule-form ::v-deep .el-form-item__label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.schedule-form ::v-deep .el-input__inner {
  border-radius: 6px;
}

.schedule-form ::v-deep .el-input__inner:hover {
  border-color: #c0c4cc;
}

.schedule-form ::v-deep .el-input__inner:focus {
  border-color: #409eff;
}

.schedule-form ::v-deep .el-textarea__inner {
  border-radius: 6px;
}

.schedule-form ::v-deep .el-textarea__inner:focus {
  border-color: #409eff;
}

.schedule-form ::v-deep .el-select .el-input__inner {
  cursor: pointer;
}

.schedule-form ::v-deep .el-radio-group {
  display: flex;
  align-items: center;
  gap: 20px;
  height: 40px;
}

.schedule-form ::v-deep .el-radio__inner {
  border-radius: 50%;
}

.schedule-form ::v-deep .el-radio__label {
  font-size: 13px;
}

/* 日期选择器样式优化 */
.schedule-form ::v-deep .el-date-editor .el-input__prefix {
  color: #909399;
}

.schedule-form ::v-deep .el-date-editor .el-input__icon {
  line-height: 29px;
}

/* 类型选择点 */
.type-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}

/* 行距优化 */
.schedule-form .el-row {
  margin-bottom: 0;
}

.schedule-form .el-col {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* 弹窗按钮样式 */
.schedule-dialog .dialog-footer ::v-deep .el-button {
  padding: 9px 20px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 13px;
}

.schedule-dialog .dialog-footer ::v-deep .el-button--default {
  background: #fff;
  border: 1px solid #dcdfe6;
  color: #606266;
}

.schedule-dialog .dialog-footer ::v-deep .el-button--default:hover {
  color: #409eff;
  border-color: #c6e2ff;
  background: #ecf5ff;
}

.schedule-dialog .dialog-footer ::v-deep .el-button--primary {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  border: none;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.schedule-dialog .dialog-footer ::v-deep .el-button--primary:hover {
  background: linear-gradient(135deg, #337ecc 0%, #409eff 100%);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
  transform: translateY(-1px);
}

.schedule-dialog .dialog-footer ::v-deep .el-button.is-loading {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
}

/* 表头编辑弹窗样式 */
.header-edit-dialog ::v-deep .el-dialog {
  border-radius: 12px;
}

.header-edit-dialog ::v-deep .el-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.header-edit-dialog ::v-deep .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.header-edit-dialog ::v-deep .el-dialog__body {
  padding: 20px 24px;
}

.header-edit-dialog ::v-deep .el-dialog__footer {
  padding: 16px 24px 20px;
  border-top: 1px solid #f0f0f0;
}

.header-edit-dialog ::v-deep .el-form-item {
  margin-bottom: 18px;
}

.header-edit-dialog ::v-deep .el-form-item__label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.header-edit-dialog ::v-deep .el-input__inner {
  border-radius: 6px;
}

.header-edit-dialog ::v-deep .el-input__inner:focus {
  border-color: #409eff;
}

/* 权限列弹窗样式 */
.auth-dialog ::v-deep .el-dialog {
  border-radius: 12px;
}

.auth-dialog ::v-deep .el-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.auth-dialog ::v-deep .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.auth-dialog ::v-deep .el-dialog__body {
  padding: 0;
}

.auth-dialog ::v-deep .el-dialog__footer {
  padding: 16px 24px 20px;
  border-top: 1px solid #f0f0f0;
}

.auth-dialog-content {
  padding: 20px 24px 24px;
}

.auth-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #ecf5ff 0%, #e6f4ff 100%);
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #b3d8ff;
}

.auth-tip i {
  color: #409eff;
  font-size: 16px;
}

.auth-tip span {
  font-size: 13px;
  color: #303133;
}

.auth-table {
  border-radius: 8px;
  overflow: hidden;
}

.auth-table ::v-deep .el-table__header th {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  color: #303133;
  font-weight: 600;
  font-size: 13px;
  height: 29px;
}

.auth-table ::v-deep .el-table__body td {
  height: 48px;
  font-size: 13px;
}

.auth-type-name {
  font-weight: 500;
  color: #303133;
}

.auth-dialog ::v-deep .el-switch {
  font-size: 12px;
}

.auth-dialog ::v-deep .el-switch__label {
  color: #606266;
}

.auth-dialog ::v-deep .el-switch__label--active {
  color: #409eff;
}
</style>
